import { SUBSTEP_TYPE, isDisclosureRadioItems } from "utils/constants/onboarding";

const VERIFIED_ON_SAVE_KEYS = new Set(["contact_phone", "contact_email"]);

export const buildInitialDraftValues = (flow) => {
  const values = {};
  const steps = flow?.flow_config?.steps ?? [];

  const processSubstep = (sub) => {
    if (!sub) return;
    if (sub.substep_type === SUBSTEP_TYPE.TEXT_INPUT && sub.input_text?.value != null) {
      const stringValue = sub.input_text.value;
      const isContactKey = VERIFIED_ON_SAVE_KEYS.has(sub.key);
      values[sub.substep_id] = {
        string_value: stringValue,
        // Only saved contacts are treated as verified.
        ...(isContactKey && stringValue ? { verified: true } : {}),
      };
    } else if (
      sub.substep_type === SUBSTEP_TYPE.IMAGE_INPUT &&
      sub.input_image?.value != null
    ) {
      values[sub.substep_id] = { string_list_value: sub.input_image.value };
    } else if (sub.substep_type === SUBSTEP_TYPE.TODO_LIST && sub.input_todo_list?.items) {
      const items = sub.input_todo_list.items;
      const hasPrimitives = items.some((it) => (it.primitive_inputs ?? []).length > 0);
      if (!hasPrimitives) {
        // Patched radio items return checked, so restore the "No" choice when present.
        const checkedItems = items.filter((it) => it.checked);
        const selectedItem = checkedItems.find((it) => {
          const k = (it.key ?? "").toLowerCase();
          const t = (it.title ?? "").toLowerCase().trim();
          return t === "no" || k.endsWith("_no");
        }) ?? checkedItems[0];
        if (selectedItem) values[selectedItem.substep_id] = { bool_value: true };
      } else {
        for (const item of items) {
          if (item.checked) values[item.substep_id] = { bool_value: true };
          for (const prim of item.primitive_inputs ?? []) {
            processSubstep(prim);
          }
        }
      }
    }
  };

  for (const step of steps) {
    for (const sub of step.substeps ?? []) processSubstep(sub);
  }
  return values;
};

// Keep omitted disclosure items from defaulting to false.
export const patchDisclosureValuesForStep = (step, valuesArray) => {
  const result = [...valuesArray];
  const patchedIds = new Set(result.map((v) => v.substep_id));
  for (const sub of step?.substeps ?? []) {
    if (sub.substep_type !== SUBSTEP_TYPE.TODO_LIST) continue;
    const items = sub.input_todo_list?.items ?? [];
    const hasPrimitives = items.some((it) => (it.primitive_inputs ?? []).length > 0);
    if (!isDisclosureRadioItems(items) && hasPrimitives) continue;
    for (const item of items) {
      if (!patchedIds.has(item.substep_id)) {
        result.push({ substep_id: item.substep_id, bool_value: true });
        patchedIds.add(item.substep_id);
      }
    }
  }
  return result;
};

// Keep omitted form-group items from defaulting to false.
export const patchFormGroupTodoItems = (step, valuesArray) => {
  const result = [...valuesArray];
  const patchedIds = new Set(result.map((v) => v.substep_id));
  for (const sub of step?.substeps ?? []) {
    if (sub.substep_type !== SUBSTEP_TYPE.TODO_LIST) continue;
    const items = sub.input_todo_list?.items ?? [];
    const hasPrimitives = items.some((it) => (it.primitive_inputs ?? []).length > 0);
    if (!hasPrimitives || isDisclosureRadioItems(items)) continue;
    for (const item of items) {
      if (!patchedIds.has(item.substep_id)) {
        result.push({ substep_id: item.substep_id, bool_value: true });
        patchedIds.add(item.substep_id);
      }
    }
  }
  return result;
};

// Apply both TODO value patches.
export const patchStepValues = (step, valuesArray) =>
  patchDisclosureValuesForStep(step, patchFormGroupTodoItems(step, valuesArray));

// Skip unverified contacts, empty text and false booleans.
// Keep empty image lists so saved images can be cleared.
export const toValuesArray = (initialValues, editedValues) => {
  const merged = { ...initialValues, ...editedValues };
  return Object.entries(merged)
    .filter(([, val]) => {
      if (val == null || val?.verified === false) return false;
      const hasText = (val?.string_value ?? "").trim() !== "";
      // An empty list clears the saved image.
      const hasImages = Array.isArray(val?.string_list_value);
      const hasBool = val?.bool_value === true;
      return hasText || hasImages || hasBool;
    })
    .map(([id, val]) => {
      const clean = { ...val };
      delete clean._previewUrls;
      delete clean.verified;
      return { substep_id: Number(id), ...clean };
    });
};
